import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import userAvatar from 'assets/img/user-avatar.png';
import Avatar from 'components/common/Avatar';
import IconButton from 'components/common/IconButton';
import Editor from 'components/editor/Editor';
import ChangePassword from 'components/pages/user/settings/ChangePassword';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Nav, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import MaskedInput from 'react-text-mask';
import AgentPhotoService from 'services/AgentPhoto.service';
import AgentsService from 'services/Agents.service';

const ProfileUser = () => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { authenticatedUser } = useAuthenticatedUser();
  const [activeTab, setActiveTab] = useState('personal-data');
  const [isLoading, setIsLoading] = useState(false);

  // Photo State
  const [userPhoto, setUserPhoto] = useState({ url: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const inputFile = useRef(null);
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);

  // Profile Data State
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Signature State
  const [signature, setSignature] = useState('');

  // Alerts
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlertError, setShowAlertError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (authenticatedUser?.id) {
      loadProfileData();
      loadUserPhoto();
      loadSignature();
    }
  }, [authenticatedUser]);

  useEffect(() => {
    if (selectedFile) {
      updatePhoto();
    }
  }, [selectedFile]);

  const loadProfileData = async () => {
    setIsLoading(true);
    try {
      const response = await AgentsService.myProfile();
      setProfileData({
        firstName: response.firstName || '',
        lastName: response.lastName || '',
        email: response.email || '',
        phone: response.phone || ''
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserPhoto = async () => {
    setIsPhotoLoading(true);
    try {
      const request = await AgentPhotoService.find(authenticatedUser.id);
      if (request.ok) {
        const response = await request.json();
        setUserPhoto(response);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsPhotoLoading(false);
    }
  };

  const loadSignature = async () => {
    try {
      const response = await AgentsService.getSignature();
      setSignature(response.signature || '');
    } catch (e) {
      console.error(e);
    }
  };

  const updatePhoto = async () => {
    setIsPhotoLoading(true);
    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);

      try {
        const response = await AgentPhotoService.update(
          authenticatedUser.id,
          formData
        );
        if (response.status && response.status !== 200) {
          showErrorNotification(response.userMessage);
        } else {
          await loadUserPhoto();
          showSuccessNotification('Foto de perfil alterada com sucesso');
          setSelectedFile(null);
        }
      } catch (e) {
        showErrorNotification('Erro ao atualizar foto');
      }
    }
    setIsPhotoLoading(false);
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Tem certeza que deseja remover a foto?')) return;

    setIsPhotoLoading(true);
    try {
      const request = await AgentPhotoService.delete(authenticatedUser.id);
      if (request.ok) {
        showSuccessNotification('Foto removida com sucesso');
        loadUserPhoto();
      } else {
        showErrorNotification('Erro ao remover foto');
      }
    } catch (e) {
      showErrorNotification('Erro ao remover foto');
    } finally {
      setIsPhotoLoading(false);
    }
  };

  const handleProfileUpdate = async e => {
    e.preventDefault();
    try {
      await AgentsService.updateMyProfile(profileData);
      showSuccessNotification('Dados pessoais atualizados com sucesso');
    } catch (e) {
      showErrorNotification(e.userMessage || 'Erro ao atualizar perfil');
    }
  };

  const handleSignatureUpdate = async () => {
    try {
      await AgentsService.updateMySignature({ signature });
      showSuccessNotification('Assinatura atualizada com sucesso');
    } catch (e) {
      showErrorNotification(e.userMessage || 'Erro ao atualizar assinatura');
    }
  };

  const scrollToSection = sectionId => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setActiveTab(sectionId);
    }
  };

  return (
    <>
      <SuccessRequest
        message={alertMessage}
        showAlert={showAlert}
        setShowAlert={() => setShowAlert(false)}
      />
      <FailedRequest
        message={errorMessage}
        showAlert={showAlertError}
        setShowAlert={() => setShowAlertError(false)}
      />

      <Card style={{ minHeight: '85vh' }}>
        <Card.Body>
          <Row className="h-100">
            <Col
              md={3}
              className="d-flex flex-column align-items-center border-end h-100"
              style={{ position: 'sticky', top: '20px', height: 'fit-content' }}
            >
              <div className="mb-4 text-center">
                {isPhotoLoading ? (
                  <Skeleton circle height={120} width={120} />
                ) : (
                  <div className="position-relative d-inline-block">
                    <Avatar
                      size="4xl"
                      src={userPhoto.url || userAvatar}
                      mediaClass="img-thumbnail rounded-circle shadow-sm"
                    />
                    <div className="position-absolute bottom-0 end-0">
                      <IconButton
                        variant="falcon-default"
                        size="sm"
                        icon="camera"
                        className="me-1"
                        onClick={() => inputFile.current.click()}
                      />
                      <IconButton
                        variant="falcon-default"
                        size="sm"
                        icon="trash"
                        onClick={handleDeletePhoto}
                      />
                    </div>
                    <input
                      type="file"
                      ref={inputFile}
                      onChange={e => setSelectedFile(e.target.files[0])}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
                <h5 className="mt-3 mb-0">
                  {profileData.firstName} {profileData.lastName}
                </h5>
              </div>

              <Nav variant="pills" className="flex-column w-100">
                <div
                  key="personal-data"
                  active={activeTab === 'personal-data'}
                  onClick={() => scrollToSection('personal-data')}
                  className={`mb-2 p-2 text-body-secondary ${
                    activeTab === 'personal-data'
                      ? 'text-primary btn-falcon-default rounded'
                      : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                >
                  Dados Pessoais
                </div>
                <div
                  key="signature"
                  active={activeTab === 'signature'}
                  onClick={() => scrollToSection('signature')}
                  className={`mb-2 p-2 text-body-secondary ${
                    activeTab === 'signature'
                      ? 'text-primary btn-falcon-default rounded'
                      : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                >
                  Assinatura de E-mail
                </div>
                <div
                  key="security"
                  active={activeTab === 'security'}
                  onClick={() => scrollToSection('security')}
                  className={`mb-2 p-2 text-body-secondary ${
                    activeTab === 'security'
                      ? 'text-primary btn-falcon-default rounded'
                      : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                >
                  Segurança
                </div>
              </Nav>
            </Col>

            <Col md={9} className="p-4" style={{ overflowY: 'auto' }}>
              <div id="personal-data" className="mb-5">
                <h5 className="mb-4">Dados Pessoais</h5>
                <Form onSubmit={handleProfileUpdate}>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Label>Nome</Form.Label>
                      <Form.Group>
                        <Form.Control
                          type="text"
                          placeholder="Nome"
                          value={profileData.firstName}
                          onChange={e =>
                            setProfileData({
                              ...profileData,
                              firstName: e.target.value
                            })
                          }
                          className="me-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sobrenome</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Sobrenome"
                          value={profileData.lastName}
                          onChange={e =>
                            setProfileData({
                              ...profileData,
                              lastName: e.target.value
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group controlId="email">
                        <Form.Label>E-mail Profissional</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="nome@exemplo.com"
                          value={profileData.email}
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="phone">
                        <Form.Label>Telefone</Form.Label>
                        <MaskedInput
                          mask={[
                            '(',
                            /[1-9]/,
                            /\d/,
                            ')',
                            ' ',
                            /\d/,
                            /\d/,
                            /\d/,
                            /\d/,
                            /\d/,
                            '-',
                            /\d/,
                            /\d/,
                            /\d/,
                            /\d/
                          ]}
                          placeholder="Telefone"
                          guide={false}
                          value={profileData.phone}
                          onChange={e =>
                            setProfileData({
                              ...profileData,
                              phone: e.target.value
                            })
                          }
                          render={(ref, props) => (
                            <Form.Control ref={ref} {...props} />
                          )}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end">
                    <Button type="submit">
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      Salvar
                    </Button>
                  </div>
                </Form>
              </div>

              <hr className="my-4" />

              <div id="signature" className="mb-5">
                <h5 className="mb-4">Assinatura de E-mail</h5>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Configure sua assinatura de e-mail</Form.Label>
                    <Editor
                      data={signature}
                      value={setSignature}
                      style={{ height: '300px' }}
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-end mt-3">
                    <Button onClick={handleSignatureUpdate}>
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      Salvar
                    </Button>
                  </div>
                </Form>
              </div>

              <hr className="my-4" />

              <div id="security" className="mb-5">
                <h5 className="mb-4">Segurança</h5>
                <ChangePassword
                  hasCard={false}
                  btnClassName="d-block ms-auto w-auto btn-success"
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default ProfileUser;
